import { useMemo, useCallback } from 'react';
import { Tree, ConfigProvider } from 'antd';
import styled from 'styled-components';
import { useCollectionStore } from '@stores/useCollectionStore';
import CollectionNode from './CollectionNode';

// When $flat=true the tree renders with no left-side indentation or switcher
// space — appropriate while the data is a flat list. Flip $flat to false (or
// remove the prop entirely) once the data gains a real hierarchy.
//
// Full-width rows give every row a shared right-hand action column. antd's
// `blockNode` prop stretches the content wrapper; `.ant-tree-title` needs the
// override because it is a span, so the row content has no block box to size
// against. Safe because `treeTheme` below makes hover and selection backgrounds
// transparent — a full-width row would otherwise paint a block across the panel.
const StyledTree = styled(Tree)`
	.ant-tree-title {
		display: block;
	}

	/* antd insets the wrapper 8px; the section header's actions sit flush. */
	.ant-tree-node-content-wrapper {
		padding-inline-end: 0;
	}

	${({ $flat }) =>
		$flat &&
		`
		.ant-tree-indent { display: none; }
		.ant-tree-switcher { display: none; }
		.ant-tree-treenode { padding-left: 0; }
	`}
`;

/**
 * Scoped antd Tree theme tokens.
 * Uses antd's component token API instead of styled-component class overrides —
 * survives antd version upgrades and avoids !important specificity fights.
 */
const treeTheme = {
	components: {
		Tree: {
			colorBgContainer: 'transparent',
			nodeHoverBg: 'transparent',
			nodeSelectedBg: 'transparent',
			titleHeight: 28, // Reduce height for compact display
		},
	},
};

/**
 * Converts the collection data objects into antd Tree's `treeData` format.
 * Produces plain objects only (no JSX) so useMemo can prevent unnecessary
 * antd Tree diffing. Rendering is delegated to `titleRender`.
 */
const buildTreeData = nodes =>
	nodes.map(node => ({
		key: node.key,
		nodeData: node,
		children: node.children?.length ? buildTreeData(node.children) : undefined,
	}));

/**
 * Renders the collection hierarchy using antd's Tree component.
 *
 * Key performance decisions:
 * - `treeData` is plain objects (memoized by `nodes` reference).
 * - `titleRender` handles presentation — antd only calls it for visible nodes.
 * - Expand/check state is controlled via the Zustand store (object maps).
 *
 * @param {object} props
 * @param {Array} props.nodes
 * @param {(key: string, pointer: string) => void} props.onPointerChange
 * @param {(key: string, hex: string, opacity: number) => void} props.onColorPreview
 * @param {(key: string, hex: string, opacity: number) => void} props.onColorCommit
 * @param {() => void} props.onColorCancel
 */
const CollectionTree = ({
	nodes,
	onPointerChange,
	onColorPreview,
	onColorCommit,
	onColorCancel,
}) => {
	const isFlat = useMemo(() => nodes.every(node => !node.children?.length), [nodes]);
	const expandedNodes = useCollectionStore(s => s.expandedNodes);
	const checkedNodes = useCollectionStore(s => s.checkedNodes);
	const expandAll = useCollectionStore(s => s.expandAll);
	const checkAll = useCollectionStore(s => s.checkAll);

	const treeData = useMemo(() => buildTreeData(nodes), [nodes]);

	const handleExpand = useCallback(expandedKeys => expandAll(expandedKeys), [expandAll]);

	const handleCheck = useCallback(
		checkedKeys => {
			const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
			checkAll(keys);
		},
		[checkAll]
	);

	const titleRender = useCallback(
		nodeRecord => (
			<CollectionNode
				node={nodeRecord.nodeData}
				onPointerChange={onPointerChange}
				onColorPreview={onColorPreview}
				onColorCommit={onColorCommit}
				onColorCancel={onColorCancel}
			/>
		),
		[onPointerChange, onColorPreview, onColorCommit, onColorCancel]
	);

	return (
		<ConfigProvider theme={treeTheme}>
			<StyledTree
				$flat={isFlat}
				indentSize={isFlat ? 0 : undefined}
				treeData={treeData}
				checkable
				blockNode
				selectable={false}
				expandedKeys={Object.keys(expandedNodes)}
				checkedKeys={Object.keys(checkedNodes)}
				onExpand={handleExpand}
				onCheck={handleCheck}
				titleRender={titleRender}
			/>
		</ConfigProvider>
	);
};

export default CollectionTree;
